import {
  Brand,
  FeeSchedule,
  ManualTransactionType,
  Organisation,
  ServiceProvider,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import {
  IReportFilterContext,
  IServiceProvider,
  MentionResourceType,
  ReportFilterOption,
  ReportFilterOptionResolver,
} from '@principle-theorem/principle-core/interfaces';
import { SERVICE_PROVIDER_FEATURE_MAP } from '@principle-theorem/principle-core/features';
import {
  asyncForEach,
  DocumentData,
  Firestore,
  getEnumValues,
  Query,
  splitCamel,
  titlecase,
  undeletedFilter,
} from '@principle-theorem/shared';
import { isFunction, toPairs } from 'lodash';

interface IHasName {
  name: string;
}

function getName(data: IHasName): string {
  return data.name;
}

// function prefixName(prefix: string): (data: IHasName) => string {
//   return (data: IHasName) => `(${prefix}) ${getName(data)}`;
// }

function suffixName(suffix: string): (data: IHasName) => string {
  return (data: IHasName) => `${getName(data)} (${suffix})`;
}

async function optionsFromCollection<IDocumentData extends DocumentData>(
  colRef: Query<IDocumentData>,
  getNameFn: (data: IDocumentData) => string,
  filterFn?: (data: IDocumentData) => boolean,
  undefinedValue?: string
): Promise<ReportFilterOption[]> {
  const docs = await Firestore.getDocs(colRef);
  const filtered = filterFn ? docs.filter(filterFn) : docs;
  const options = filtered.map((doc) => {
    const name = getNameFn(doc);
    return {
      label: name,
      value: doc.ref.path,
      isDeleted: 'deleted' in doc && doc.deleted === true ? true : false,
    };
  });

  if (!undefinedValue) {
    return options;
  }

  return [
    {
      label: undefinedValue,
      value: UNDEFINED_VALUE,
    },
    ...options,
  ];
}

export const UNDEFINED_VALUE = 'UNDEFINED_VALUE';

export class QueryOptionResolver {
  static async resolve(
    context: IReportFilterContext,
    optionResolver?: ReportFilterOptionResolver
  ): Promise<ReportFilterOption[]> {
    if (!optionResolver) {
      return [];
    }
    if (!isFunction(optionResolver)) {
      return optionResolver;
    }
    return optionResolver(context);
  }

  static values<T extends string | number>(
    values: T[],
    undefinedValue?: string
  ): ReportFilterOption[] {
    const options = values.map((value) => ({
      label: titlecase(splitCamel(`${value}`)),
      value: String(value),
    }));

    if (!undefinedValue) {
      return options;
    }

    return [
      {
        label: undefinedValue,
        value: UNDEFINED_VALUE,
      },
      ...options,
    ];
  }

  static enum(item: unknown, undefinedValue?: string): ReportFilterOption[] {
    return this.values(getEnumValues(item), undefinedValue);
  }

  static boolean(): ReportFilterOption[] {
    return [
      {
        label: 'Yes',
        value: true,
      },
      {
        label: 'No',
        value: false,
      },
    ];
  }

  static users(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Organisation.userCol(context.organisation),
        getName
      );
  }

  static brands(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Organisation.brandCol(context.organisation),
        getName
      );
  }

  static practices(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(Brand.practiceCol(context.brand), getName);
  }

  static staff(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.stafferCol(context.brand),
        (staffer) => staffer.user.name
      );
  }

  static patients(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.patientCol(context.brand),
        getName,
        undeletedFilter
      );
  }

  static feeSchedules(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      feeScheduleOptionResolver(context);
  }

  static schedulingEventReasons(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.cancellationReasonCol(context.brand),
        getName,
        undeletedFilter
      );
  }

  static labs(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.labCol(context.brand),
        getName,
        undeletedFilter
      );
  }

  static treatmentCategories(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.treatmentCategoryCol(context.brand),
        (treatmentCategory) => treatmentCategory.name,
        undefined,
        'No Category'
      );
  }

  static patientTags(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.patientTagCol(context.brand),
        (tag) => tag.name
      );
  }

  static appointmentTags(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.appointmentTagCol(context.brand),
        (tag) => tag.name
      );
  }

  static patientNoteTags(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.patientNoteTagCol(context.brand),
        (tag) => tag.name
      );
  }

  static serviceCodeType(context: IReportFilterContext): ReportFilterOption[] {
    return QueryOptionResolver._getAllowedProviders(context).map(
      (provider) => ({
        label: provider.label,
        value: provider.type,
      })
    );
  }

  static serviceCodes(context: IReportFilterContext): ReportFilterOption[] {
    return QueryOptionResolver._getAllowedProviders(context)
      .map((provider) => toPairs(provider.items))
      .flat()
      .map(([key, code]) => ({
        label: `${code.code}`,
        value: key,
      }));
  }

  static transactionSubTypes(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        ManualTransactionType.col(context.brand),
        (tag) => tag.name
      );
  }

  static treatments(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        TreatmentConfiguration.col(context.brand),
        (treatment) => treatment.name
      );
  }

  static products(): ReportFilterOptionResolver {
    return (context: IReportFilterContext) =>
      optionsFromCollection(
        Brand.productCol(context.brand),
        (product) => product.name
      );
  }

  static referrerTypes(): ReportFilterOptionResolver {
    return async (context: IReportFilterContext) => {
      const referralSources = await optionsFromCollection(
        Brand.referralSourceCol(context.brand),
        (referralSource) => referralSource.name,
        undeletedFilter
      );
      const patientReferralType: ReportFilterOption = {
        label: 'Patient',
        value: MentionResourceType.Patient,
      };
      return [...referralSources, patientReferralType];
    };
  }

  static referrerNames(): ReportFilterOptionResolver {
    return async (context: IReportFilterContext) => {
      const referralSources = await optionsFromCollection(
        Brand.referralSourceCol(context.brand),
        (referralSource) => referralSource.name,
        undeletedFilter
      );
      const patientNames = await optionsFromCollection(
        Brand.patientCol(context.brand),
        (patient) => patient.name,
        undeletedFilter
      );
      return [referralSources, patientNames].flat();
    };
  }

  private static _getAllowedProviders(
    context: IReportFilterContext
  ): IServiceProvider[] {
    const providers = ServiceProvider.providersByRegion(
      context.organisation.region
    );
    return providers.filter((provider) => {
      const featureLabel = SERVICE_PROVIDER_FEATURE_MAP[provider.type];
      return (
        !featureLabel ||
        (context.organisation.optInFeatures ?? []).includes(featureLabel)
      );
    });
  }
}

async function feeScheduleOptionResolver(
  context: IReportFilterContext
): Promise<ReportFilterOption[]> {
  const orgOptions = await optionsFromCollection(
    FeeSchedule.col(context.organisation),
    suffixName(`Organisation`)
  );

  const practices = await Firestore.getDocs(Brand.practiceCol(context.brand));
  const practiceOptions = await asyncForEach(practices, async (practice) =>
    optionsFromCollection(
      FeeSchedule.col(practice),
      suffixName(`${practice.name}`)
    )
  );

  const staff = await Firestore.getDocs(Brand.stafferCol(context.brand));
  const staffOptions = await asyncForEach(staff, async (staffer) =>
    optionsFromCollection(
      FeeSchedule.col(staffer),
      suffixName(`${staffer.user.name}`)
    )
  );

  const noneOption = { label: 'None', value: undefined };
  return [
    orgOptions,
    practiceOptions.flat(),
    staffOptions.flat(),
    noneOption,
  ].flat();
}
